import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Map, Pencil, Plus, Star, MoreHorizontal, Trash2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CareerCard } from "./CareerCard";
import { MILESTONE_STATUSES, MILESTONE_STATUS_META } from "@/lib/careerConfig";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

export function VisionBlock({ plan, playerId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(plan.vision || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!value.trim()) { toast.error(tfr("La vision carrière est requise")); return; }
    setSaving(true);
    try {
      await api.patch(`/players/${playerId}/career-plan`, { vision: value.trim() });
      toast.success(tfr("Vision mise à jour"));
      setEditing(false);
      onChanged?.();
    } catch { toast.error(tfr("Impossible de mettre à jour la vision")); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="mb-5">
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={3}
          maxLength={1000}
          className="text-sm"
          data-testid="vision-edit-textarea"
        />
        <div className="flex gap-2 mt-2">
          <Button size="sm" onClick={save} disabled={saving || !value.trim()} className="h-8 gap-1" data-testid="vision-save-btn">
            <Check className="h-3.5 w-3.5" /> Enregistrer
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setValue(plan.vision || ""); }} className="h-8 gap-1" data-testid="vision-cancel-btn">
            <X className="h-3.5 w-3.5" /> Annuler
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="group relative rounded-xl border border-border bg-secondary/40 p-4 mb-5">
      <p className="text-sm leading-relaxed text-foreground/90 pr-8" data-testid="career-vision-text">{plan.vision}</p>
      <button
        onClick={() => setEditing(true)}
        className="absolute top-3 right-3 rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40"
        aria-label={tfr("Modifier la vision carrière")}
        data-testid="vision-edit-btn"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function MilestoneDialog({ open, onOpenChange, playerId, onChanged }) {
  const [title, setTitle] = useState("");
  const [month, setMonth] = useState("6");
  const [status, setStatus] = useState("upcoming");
  const [isKey, setIsKey] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/players/${playerId}/career-plan/milestones`, {
        title: title.trim(), target_month: Number(month) || 0, status, is_key: isKey,
      });
      toast.success(tfr("Jalon ajouté"));
      onOpenChange(false);
      setTitle(""); setMonth("6"); setStatus("upcoming"); setIsKey(false);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tfr("Impossible d'ajouter le jalon")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="milestone-dialog">
        <DialogHeader>
          <DialogTitle>{tfr("Nouveau jalon")}</DialogTitle>
          <DialogDescription>{tfr("Ajoutez une étape à la feuille de route.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ms-title">Titre</Label>
            <Input id="ms-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder={tfr("Ex. Signer dans un club de Ligue 2")} data-testid="milestone-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ms-month">{tfr("Échéance (mois)")}</Label>
              <Input id="ms-month" type="number" min={0} max={120} value={month} onChange={(e) => setMonth(e.target.value)} data-testid="milestone-month-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{tfr("Statut")}</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="milestone-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MILESTONE_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={isKey} onCheckedChange={(v) => setIsKey(!!v)} data-testid="milestone-key-checkbox" />
            {tfr("Jalon clé")}
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tfr("Annuler")}</Button>
          <Button onClick={save} disabled={saving || !title.trim()} data-testid="milestone-save-btn">{tfr("Ajouter le jalon")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MilestoneRow({ m, isLast, playerId, onChanged }) {
  const meta = MILESTONE_STATUS_META[m.status] || MILESTONE_STATUS_META.upcoming;

  const setStatus = async (status) => {
    try {
      await api.patch(`/players/${playerId}/career-plan/milestones/${m.id}`, { status });
      toast.success(tfr("Jalon mis à jour"));
      onChanged?.();
    } catch { toast.error(tfr("Impossible de mettre à jour le jalon")); }
  };
  const remove = async () => {
    try {
      await api.delete(`/players/${playerId}/career-plan/milestones/${m.id}`);
      toast.success(tfr("Jalon supprimé"));
      onChanged?.();
    } catch { toast.error(tfr("Impossible de supprimer le jalon")); }
  };

  return (
    <li className="relative pl-9" data-testid={`milestone-row-${m.id}`}>
      {!isLast && <span className="absolute left-[10px] top-6 bottom-[-8px] w-px bg-border" aria-hidden="true" />}
      <span className={cn("absolute left-[3px] top-1.5 h-[15px] w-[15px] rounded-full border-2", meta.dot)} aria-hidden="true" />
      <div className="flex items-start justify-between gap-2 pb-5">
        <div className="min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-[11px] font-mono font-semibold text-muted-foreground bg-secondary rounded px-1.5 py-0.5">M+{m.target_month}</span>
            <p className="text-sm font-medium text-foreground/95">{m.title}</p>
            {m.is_key && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-athlynx" title={tfr("Jalon clé")}>
                <Star className="h-3 w-3 fill-current" aria-hidden="true" /> CLÉ
              </span>
            )}
          </div>
          <Badge variant="outline" className={cn("mt-1.5 text-[10px] px-1.5 py-0", meta.badge)}>{meta.label}</Badge>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40"
              aria-label={`${tfr("Actions sur le jalon")} ${m.title}`}
              data-testid={`milestone-menu-${m.id}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{tfr("Changer le statut")}</DropdownMenuLabel>
            {MILESTONE_STATUSES.filter((s) => s.id !== m.status).map((s) => (
              <DropdownMenuItem key={s.id} onClick={() => setStatus(s.id)} data-testid={`milestone-set-${s.id}-${m.id}`}>
                {s.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={remove} className="text-danger focus:text-danger" data-testid={`milestone-delete-${m.id}`}>
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}

export function RoadmapCard({ plan, milestones, playerId, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);
  return (
    <CareerCard
      icon={Map}
      title={tfr("Vision carrière")}
      subtitle={`Feuille de route sur ${plan.horizon_months} mois`}
      testid="roadmap-card"
      right={
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="h-8 gap-1 text-xs" data-testid="milestone-add-btn">
          <Plus className="h-3.5 w-3.5" /> Jalon
        </Button>
      }
    >
      <VisionBlock plan={plan} playerId={playerId} onChanged={onChanged} />
      {milestones.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">{tfr("Aucun jalon défini. Ajoutez la première étape de la feuille de route.")}</p>
      ) : (
        <ol className="mt-1">
          {milestones.map((m, i) => (
            <MilestoneRow key={m.id} m={m} isLast={i === milestones.length - 1} playerId={playerId} onChanged={onChanged} />
          ))}
        </ol>
      )}
      <MilestoneDialog open={addOpen} onOpenChange={setAddOpen} playerId={playerId} onChanged={onChanged} />
    </CareerCard>
  );
}
